import React from "react";
import { Avatar, Tag } from "antd";
import dayjs from "dayjs";
import { LuClock, LuPaperclip } from "react-icons/lu";
import CalendarDateChip from "./CalendarDateChip";

const relativeTime = require("dayjs/plugin/relativeTime");
const utc = require("dayjs/plugin/utc");

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * One queued item in a "Scheduled" list — an email or a message.
 *
 * Leads with WHEN it sends (a tear-off calendar chip plus a relative
 * countdown), because that is the one thing a scheduled list exists to answer.
 * Shared so every scheduled surface reads the same.
 *
 * @param {string}    sendAt          UTC timestamp the item goes out at.
 * @param {ReactNode} meta            Small line above the title (recipient, author…).
 * @param {string}    avatarText      Seed for the avatar initials; hides the avatar when empty.
 * @param {ReactNode} title           Main line — subject, or the message's first line.
 * @param {ReactNode} preview         Muted body preview.
 * @param {number}    attachmentCount Shows a paperclip + count when > 0.
 * @param {ReactNode} actions         Right-hand controls (a Dropdown, icon buttons…).
 * @param {Function}  onClick         Makes the whole card clickable when provided.
 */
const ScheduledItemCard = ({
  sendAt,
  meta,
  avatarText,
  title,
  preview,
  attachmentCount = 0,
  actions,
  onClick,
  // Off when the caller renders its own expand/collapse inside `preview` —
  // clamping would fight the "more/less" toggle.
  clampPreview = true,
  className = "",
}) => {
  const localSendAt = sendAt ? dayjs.utc(sendAt).local() : null;
  const isValid = !!localSendAt?.isValid();
  // Past its slot but still listed — the worker has not picked it up yet.
  const isOverdue = isValid && localSendAt.isBefore(dayjs());

  return (
    <div
      onClick={onClick}
      className={`group flex items-stretch gap-3 p-3 mb-2 bg-white border border-solid border-grey-200 rounded-lg transition-colors hover:border-brand-300 hover:bg-brand-50/40 ${
        onClick ? "cursor-pointer" : ""
      } ${className}`}
    >
      <CalendarDateChip date={localSendAt} tone={isOverdue ? "warning" : "brand"} />

      <div className="flex flex-col min-w-0 flex-1 gap-1">
        {(meta || avatarText || attachmentCount > 0) && (
          <div className="flex items-center gap-2 min-w-0">
            {avatarText && (
              <Avatar size={20} className="bg-grey-700 shrink-0 text-[10px]">
                {String(avatarText).charAt(0).toUpperCase()}
              </Avatar>
            )}
            {meta && <span className="text-xs text-grey-500 truncate">{meta}</span>}
            {attachmentCount > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-grey-500 shrink-0">
                <LuPaperclip size={12} strokeWidth={1.75} />
                {attachmentCount}
              </span>
            )}
          </div>
        )}
        {title && <span className="text-sm font-medium text-grey-800 truncate">{title}</span>}
        {preview && (
          <span className={`text-xs text-grey-500 ${clampPreview ? "line-clamp-2" : ""}`}>
            {preview}
          </span>
        )}
      </div>

      <div className="flex flex-col items-end justify-between shrink-0 gap-2">
        {actions}
        {/* Once the slot has passed there is nothing useful to say — the send is
            out of the UI's hands, so show no tag rather than guess at a status. */}
        {isValid && !isOverdue && (
          <Tag
            className="m-0 rounded-full"
            color="processing"
            icon={<LuClock size={11} strokeWidth={2} className="mr-1 inline align-[-1px]" />}
          >
            {localSendAt.fromNow()}
          </Tag>
        )}
      </div>
    </div>
  );
};

export default ScheduledItemCard;
